import React, { useCallback, useMemo, useState } from "react";
import api from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import RefreshControl from "@/components/RefreshControl";
import useAutoRefresh from "@/lib/useAutoRefresh";
import StageBadge from "@/components/StageBadge";
import { STEPS, ROLE_LABELS } from "@/lib/stages";
import { fetchOrdersWithItems } from "@/lib/fetchOrdersWithItems";
import {
  ChartBar, Trophy, Calendar, DownloadSimple, User,
} from "@phosphor-icons/react";

const RANGE_PRESETS = [
  { key: "today", label: "Today", days: 0 },
  { key: "7d", label: "Last 7 days", days: 7 },
  { key: "30d", label: "Last 30 days", days: 30 },
  { key: "all", label: "All time", days: null },
];

/**
 * Owner-facing productivity dashboard.
 * Derives per-user scan activity from /orders → items[].progress[].
 * Zero extra backend calls beyond the orders endpoint already used elsewhere.
 */
export default function Productivity() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("7d");

  const loadOrders = useCallback(async () => {
    try {
      // List endpoint omits items[]; enrich via parallel detail fetches so
      // per-user scan counts, leaderboard, and 14-day chart have data.
      const enriched = await fetchOrdersWithItems();
      setOrders(enriched);
    } finally { setLoading(false); }
  }, []);

  const { refresh, refreshing, lastUpdatedAt, livePolling, setLivePolling } = useAutoRefresh(loadOrders, { initialPolling: true });

  const events = useMemo(() => flattenEvents(orders), [orders]);
  const filtered = useMemo(() => filterByRange(events, range), [events, range]);
  const byUser = useMemo(() => aggregateByUser(filtered), [filtered]);
  const byDay = useMemo(() => aggregateByDay(filtered), [filtered]);

  const exportCsv = () => {
    const header = ["user", "role", ...STEPS, "total"];
    const rows = byUser.map((u) => [
      escapeCsv(u.name),
      u.role || "",
      ...STEPS.map((s) => u.byStep[s] || 0),
      u.total,
    ]);
    const csv = [header, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `productivity_${range}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const totalScans = filtered.reduce((sum, e) => sum + (e.qty || 1), 0);
  const activeUsers = byUser.length;
  const topUser = byUser[0] || null;

  return (
    <div className="space-y-6" data-testid="productivity-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">Owner view</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl mt-1">Productivity</h1>
          <p className="text-sm text-zinc-600 dark:text-zinc-300 mt-1">
            Who is scanning what, by stage and day. Use this for staffing decisions.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1 rounded-md bg-zinc-100 dark:bg-zinc-800 p-1" data-testid="range-presets">
            {RANGE_PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setRange(p.key)}
                data-testid={`range-${p.key}`}
                className={`px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wider transition-colors ${
                  range === p.key
                    ? "bg-white dark:bg-zinc-950 text-[#C8102E] shadow-sm"
                    : "text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <RefreshControl
            onRefresh={refresh}
            refreshing={refreshing}
            lastUpdatedAt={lastUpdatedAt}
            livePolling={livePolling}
            onToggleLive={setLivePolling}
            testId="productivity-refresh"
          />
          <Button onClick={exportCsv} disabled={!byUser.length} className="bg-zinc-900 hover:bg-zinc-700 h-9" data-testid="export-csv">
            <DownloadSimple size={16} weight="bold" className="mr-1.5" /> CSV
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Kpi label="Scans in range" value={totalScans} accent="#C8102E" icon={ChartBar} />
        <Kpi label="Active scanners" value={activeUsers} accent="#3B82F6" icon={User} />
        <Kpi
          label="Top scanner"
          value={topUser ? `${topUser.name}` : "—"}
          subtitle={topUser ? `${topUser.total} scans` : ""}
          accent="#10B981"
          icon={Trophy}
        />
      </div>

      {loading ? (
        <Card className="p-12 text-center text-zinc-500">Loading productivity…</Card>
      ) : !filtered.length ? (
        <Card className="p-12 text-center text-zinc-500" data-testid="productivity-empty">
          No scan activity in this range / mode.
        </Card>
      ) : (
        <>
          {/* Leaderboard */}
          <Card className="border-zinc-200 dark:border-zinc-700 overflow-hidden" data-testid="leaderboard">
            <div className="px-5 py-3 border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50">
              <h2 className="font-heading font-bold text-base">Per-user scan counts</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 dark:bg-zinc-800/30 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">
                  <tr>
                    <th className="text-left px-5 py-2 w-12">#</th>
                    <th className="text-left px-2 py-2">User</th>
                    {STEPS.map((s) => (
                      <th key={s} className="text-right px-2 py-2">{s}</th>
                    ))}
                    <th className="text-right px-5 py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {byUser.map((u, i) => {
                    const maxTotal = byUser[0]?.total || 1;
                    const widthPct = Math.max(2, Math.round((u.total / maxTotal) * 100));
                    return (
                      <tr key={u.name + i} className="border-t border-zinc-200 dark:border-zinc-700" data-testid={`leader-row-${i}`}>
                        <td className="px-5 py-3 font-mono text-zinc-400">{i + 1}</td>
                        <td className="px-2 py-3">
                          <div className="font-semibold">{u.name}</div>
                          {u.role && (
                            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                              {ROLE_LABELS[u.role] || u.role}
                            </div>
                          )}
                          <div className="mt-1.5 h-1.5 rounded-full bg-zinc-100 dark:bg-zinc-800 overflow-hidden w-40 max-w-full">
                            <div className="h-full bg-[#C8102E]" style={{ width: `${widthPct}%` }} />
                          </div>
                        </td>
                        {STEPS.map((s) => (
                          <td key={s} className="text-right px-2 py-3 font-mono tabular-nums">
                            {u.byStep[s] ? (
                              <span className="font-semibold">{u.byStep[s]}</span>
                            ) : (
                              <span className="text-zinc-300 dark:text-zinc-600">—</span>
                            )}
                          </td>
                        ))}
                        <td className="text-right px-5 py-3 font-mono font-bold tabular-nums">{u.total}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Daily bar chart */}
          <Card className="border-zinc-200 dark:border-zinc-700 overflow-hidden" data-testid="daily-chart">
            <div className="px-5 py-3 border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 flex items-center gap-2">
              <Calendar size={16} weight="bold" className="text-[#C8102E]" />
              <h2 className="font-heading font-bold text-base">Daily activity</h2>
            </div>
            <DailyChart days={byDay} />
          </Card>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, subtitle, accent, icon: Icon }) {
  return (
    <Card className="p-5 border-zinc-200 dark:border-zinc-700" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">{label}</div>
          <div className="mt-2 text-3xl font-heading font-black truncate">{value}</div>
          {subtitle && <div className="text-xs text-zinc-500 mt-0.5">{subtitle}</div>}
        </div>
        <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0" style={{ background: `${accent}22`, color: accent }}>
          <Icon size={22} weight="bold" />
        </div>
      </div>
    </Card>
  );
}

function DailyChart({ days }) {
  if (!days.length) {
    return <div className="p-8 text-center text-zinc-500">No data.</div>;
  }
  const max = Math.max(...days.map((d) => d.total), 1);
  return (
    <div className="px-5 py-5">
      <div className="flex items-end gap-2 h-44">
        {days.map((d) => {
          const h = Math.max(3, Math.round((d.total / max) * 100));
          return (
            <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-1.5 min-w-0" data-testid={`day-${d.date}`}>
              <div className="text-[10px] font-mono text-zinc-500 tabular-nums">{d.total || ""}</div>
              <div
                className="w-full bg-[#C8102E] rounded-t transition-all hover:opacity-80"
                style={{ height: `${h}%`, minHeight: 4 }}
                title={`${d.date}: ${d.total} scans`}
              />
            </div>
          );
        })}
      </div>
      <div className="flex gap-2 mt-2">
        {days.map((d) => (
          <div key={`l-${d.date}`} className="flex-1 text-center text-[10px] text-zinc-500 font-mono truncate">
            {formatDayLabel(d.date)}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---- helpers ---- */

function flattenEvents(orders) {
  const out = [];
  (orders || []).forEach((order) => {
    (order.items || []).forEach((item) => {
      (item.progress || []).forEach((step) => {
        if (step.completed && step.last_timestamp && step.last_user_name) {
          // qty = physical units advanced at this step. Fall back through
          // count → total → units_total → 1 so missing fields never zero
          // out a legitimate scan.
          const qty = Number(step.count ?? step.total ?? item.units_total ?? 1) || 1;
          out.push({
            user: step.last_user_name,
            role: step.last_user_role || null,
            step: step.step,
            at: step.last_timestamp,
            qty,
          });
        }
      });
    });
  });
  return out;
}

function filterByRange(events, range) {
  if (range === "all") return events;
  const now = new Date();
  if (range === "today") {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    return events.filter((e) => new Date(e.at).getTime() >= start);
  }
  const days = range === "7d" ? 7 : range === "30d" ? 30 : 0;
  const start = now.getTime() - days * 24 * 60 * 60 * 1000;
  return events.filter((e) => new Date(e.at).getTime() >= start);
}

function aggregateByUser(events) {
  const map = new Map();
  events.forEach((e) => {
    const key = e.user;
    if (!map.has(key)) map.set(key, { name: e.user, role: e.role, total: 0, byStep: {} });
    const u = map.get(key);
    const qty = e.qty || 1;
    u.total += qty;
    u.byStep[e.step] = (u.byStep[e.step] || 0) + qty;
    if (!u.role && e.role) u.role = e.role;
  });
  return Array.from(map.values()).sort((a, b) => b.total - a.total);
}

function aggregateByDay(events) {
  // Build last 14 day slots (or smaller if range is short — we still show 14)
  const days = 14;
  const out = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
    out.push({ date: d.toISOString().slice(0, 10), total: 0 });
  }
  const byDate = new Map(out.map((d) => [d.date, d]));
  events.forEach((e) => {
    const key = new Date(e.at).toISOString().slice(0, 10);
    const slot = byDate.get(key);
    if (slot) slot.total += (e.qty || 1);
  });
  return out;
}

function formatDayLabel(iso) {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function escapeCsv(v) {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
